import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useController } from "react-hook-form";
import DatePicker, { registerLocale } from "react-datepicker";
import { fr } from "date-fns/locale/fr";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { getError } from '../../../utils/FormError';
import { useFormContext } from 'react-hook-form';

import "react-datepicker/dist/react-datepicker.css";
import "./InputDate.scss";

registerLocale("fr", fr);

// Renders the calendar popper straight into <body> instead of next to the input — inside a
// modal, an inline popper gets clipped/out-stacked by whatever ancestor has overflow:hidden
// or its own z-index (react-modal's overlay is 1050), no matter how high .react-datepicker-popper's
// own z-index is set. Same fix already used by ImprovedSelect/TableSearch's dropdowns.
const CalendarPortal = ({ children }) => createPortal(children, document.body);

const InputDate = ({
  name,
  placeholder = "",
  control,
  validationRules = {},
  size = "xl",
  mode = "date", // "date", "month-year", "datetime"
  className,
  // Month the calendar opens on. Without this, react-datepicker defaults to the
  // *selected* value's month — fine for a date already set long ago (e.g. a birth date),
  // but wrong for something like a report's "from"/"to" range, which should always open
  // near today regardless of what the field currently holds.
  openToDate,
}) => {
  const { register, formState: { errors } } = useFormContext();
  const {
    field: { onChange, onBlur, value, ref },
  } = useController({
    name,
    control,
    rules: validationRules,
  });
  const error = getError(errors, name);
  const datePickerRef = useRef(null);
  const wrapperRef = useRef(null);

  // On narrow screens, an anchored popper positioned relative to the input can't
  // reliably fit a calendar (and, in "datetime" mode, a stacked time list under
  // it) within the viewport in either dimension — it ends up clipped off the top
  // or bottom depending on where the input sits. withPortal renders it instead as
  // a centered full-screen overlay (react-datepicker's own built-in mobile
  // pattern), sidestepping anchor-relative positioning entirely.
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 480px)').matches);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 480px)');
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // react-datepicker closes on outside click via its own document "mousedown"
  // listener (bubble phase) — but Modal.js calls stopPropagation() on mousedown
  // for anything inside a modal dialog, so that listener never fires there.
  // This capture-phase listener runs before stopPropagation can block it.
  useEffect(() => {
    const handleClickOutside = (e) => {
      // The calendar itself is portalled to <body> (see CalendarPortal above), so it's
      // never inside wrapperRef — without this it'd count as "outside" and close on
      // every day/time click instead of just to when the user clicks off the picker.
      if (
        wrapperRef.current && !wrapperRef.current.contains(e.target)
        && !e.target.closest?.('.react-datepicker-popper')
      ) {
        datePickerRef.current?.setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, []);

  // Configuration selon le mode
  let datePickerProps = {};
  switch (mode) {
    case "month-year":
      datePickerProps = {
        showMonthYearPicker: true,
        dateFormat: "MM/yyyy",
      };
      break;
    case "datetime":
      datePickerProps = {
        showTimeSelect: true,
        timeFormat: "HH:mm",
        timeIntervals: 30,
        dateFormat: "dd/MM/yyyy HH:mm",
        timeCaption: "Heure",
      };
      break;
    case "date":
    default:
      datePickerProps = {
        dateFormat: "yyyy-MM-dd",
      };
      break;
  }

  return (
    <>
      <div className="input-date-wrapper" ref={wrapperRef}>
        <DatePicker
          ref={datePickerRef}
          selected={value ? new Date(value) : null}
          onChange={(date) => onChange(date)}
          onBlur={onBlur}
          placeholderText={placeholder}
          wrapperClassName="datepicker-wrapper"
          popperClassName={`datepicker-${size}`}
          popperContainer={CalendarPortal}
          withPortal={isMobile}
          portalId="datepicker-portal"
          locale="fr"
          openToDate={openToDate}
          {...datePickerProps}
          customInput={
            <input
              ref={ref}
              className={`form-control ${className} ${error ? "is-invalid" : ""}`}
            />
          }
        />
        <FontAwesomeIcon
          icon={faCalendarAlt}
          className="input-date-icon"
          onClick={() => datePickerRef.current?.setOpen(true)}
        />
      </div>
      {error && (
        <div className="invalid-feedback d-block">{error.message}</div>
      )}
    </>
  );
};

export default InputDate;
